import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { StyleSheet, View, ViewStyle } from 'react-native';
import { Extrapolation, interpolate, SharedValue } from 'react-native-reanimated';
import Carousel, { ICarouselInstance } from 'react-native-reanimated-carousel';

import { useWheelMotion, WheelMotionProvider } from './useTapGuard';

export const WHEEL_POSE = {
	rotateDeg: 61.2065,
	perspectiveRatio: 5.6939,
	translateZRatio: -1.4422,
	translateXRatio: 0.867163,
	skewDeg: 2.116,
	tailDistance: [0, 1, 1.2, 1.4, 1.7, 2],
	tailDepth: [0, 1, 2.094, 3.66, 7.245, 12.355],
	turnDistance: [0, 1, 2],
	turnMultiple: [0, 1, 1.35],
	fadeDistance: [0, 1, 1.9],
	fadeOpacity: [1, 0.9, 0],
};

export type WheelPose = typeof WHEEL_POSE;

// Cards fade out by 1.9 steps from the front, so only a few a side are ever on show. Lists longer
// than this mount just the cards around the front; a room's 60+ tables all mounted made the swipe lag.
const WINDOW_SIZE = 7;
const ITEM_WIDTH_SCALE = 1.1;

export type WheelCarouselHandle = {
	next: () => void;
	prev: () => void;
	scrollToIndex: (index: number) => void;
};

export type WheelCarouselProps<T> = {
	data: T[];
	renderItem: (params: { item: T; index: number }) => React.ReactNode;
	activeIndex: number;
	onActiveIndexChange: (index: number) => void;
	width: number;
	height: number;
	cardWidth: number;
	cardHeight: number;
	progress?: SharedValue<number>;
	pose?: WheelPose;
	scrollAnimationDuration?: number;
};

function WheelCarouselInner<T>(
	{
		data,
		renderItem,
		activeIndex,
		onActiveIndexChange,
		width,
		height,
		cardWidth,
		cardHeight,
		progress,
		pose = WHEEL_POSE,
		scrollAnimationDuration = 420,
	}: WheelCarouselProps<T>,
	ref: React.Ref<WheelCarouselHandle>,
) {
	const carouselRef = useRef<ICarouselInstance>(null);
	const count = data.length;
	const motion = useWheelMotion();

	useImperativeHandle(
		ref,
		() => ({
			next: () => carouselRef.current?.next(),
			prev: () => carouselRef.current?.prev(),
			scrollToIndex: (index: number) => {
				const carousel = carouselRef.current;

				if (!carousel || count <= 1) return;

				const current = ((carousel.getCurrentIndex() % count) + count) % count;
				const forward = (((index - current) % count) + count) % count;
				const delta = forward > count / 2 ? forward - count : forward;

				if (!delta) return;

				carousel.scrollTo({ count: delta, animated: true });
			},
		}),
		[count],
	);

	const perspective = cardWidth * pose.perspectiveRatio;
	const translateXAtStep = cardWidth * pose.translateXRatio;
	const translateZAtStep = cardWidth * pose.translateZRatio;

	const customAnimation = useCallback(
		(value: number): ViewStyle => {
			'worklet';

			const clamped = interpolate(value, [-2, 2], [-2, 2], Extrapolation.CLAMP);
			const distance = Math.abs(clamped);
			const side = clamped < 0 ? -1 : 1;

			const depth = interpolate(distance, pose.tailDistance, pose.tailDepth, Extrapolation.CLAMP);
			const translateZ = translateZAtStep * depth;
			// RN has no translateZ, so apply the perspective divide it would have produced.
			const depthScale = perspective / Math.max(perspective - translateZ, 1);
			const translateX = side * translateXAtStep * distance * depthScale;
			const turn = interpolate(distance, pose.turnDistance, pose.turnMultiple, Extrapolation.CLAMP);
			const rotateY = side * pose.rotateDeg * turn;
			const skewY = side * pose.skewDeg * turn;

			const opacity = interpolate(
				Math.abs(value),
				pose.fadeDistance,
				pose.fadeOpacity,
				Extrapolation.CLAMP,
			);

			return {
				zIndex: Math.round(interpolate(distance, [0, 1, 2], [300, 200, 100], Extrapolation.CLAMP)),
				opacity,
				transform: [
					{ skewY: `${skewY}deg` },
					{ perspective },
					{ translateX },
					{ scale: depthScale },
					{ rotateY: `${rotateY}deg` },
				],
			};
		},
		[
			perspective,
			pose.fadeDistance,
			pose.fadeOpacity,
			pose.rotateDeg,
			pose.skewDeg,
			pose.tailDepth,
			pose.tailDistance,
			pose.turnDistance,
			pose.turnMultiple,
			translateXAtStep,
			translateZAtStep,
		],
	);

	const renderSlot = useCallback(
		({ item, index }: { item: T; index: number }) => (
			<View
				style={[styles.itemFill, { transform: [{ scaleX: ITEM_WIDTH_SCALE }] }]}
				pointerEvents='box-none'
			>
				<View style={{ width: cardWidth, height: cardHeight }}>{renderItem({ item, index })}</View>
			</View>
		),
		[cardHeight, cardWidth, renderItem],
	);

	const sizeStyle = useMemo(() => ({ width, height }), [width, height]);

	return (
		<View style={[styles.root, sizeStyle]}>
			<WheelMotionProvider value={motion.value}>
				<Carousel
					ref={carouselRef}
					data={data}
					loop
					autoFillData
					windowSize={count > WINDOW_SIZE ? WINDOW_SIZE : undefined}
					width={width}
					height={height}
					defaultIndex={Math.min(activeIndex, Math.max(count - 1, 0))}
					snapEnabled
					pagingEnabled
					scrollAnimationDuration={scrollAnimationDuration}
					onProgressChange={progress}
					onSnapToItem={onActiveIndexChange}
					onScrollStart={motion.onScrollStart}
					onScrollEnd={motion.onScrollEnd}
					customAnimation={customAnimation}
					renderItem={renderSlot}
					containerStyle={sizeStyle}
					style={sizeStyle}
				/>
			</WheelMotionProvider>
		</View>
	);
}

const styles = StyleSheet.create({
	root: {
		alignSelf: 'center',
		zIndex: 1,
	},
	itemFill: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
});

export const WheelCarousel = forwardRef(WheelCarouselInner) as <T>(
	props: WheelCarouselProps<T> & { ref?: React.Ref<WheelCarouselHandle> },
) => React.ReactElement;

export default WheelCarousel;
